import Resource from "./resource";
import request from "@/utils/request";

// ──────────────────────────────────────────────
// Ressources CRUD
// ──────────────────────────────────────────────
export const brandsApi         = new Resource("gestStock/brands");
export const productTypesApi   = new Resource("gestStock/product-types");
export const productModelsApi  = new Resource("gestStock/product-models");
export const productsApi       = new Resource("gestStock/products");
export const productItemsApi   = new Resource("gestStock/product-items");
export const locationsApi      = new Resource("gestStock/locations");
export const purchaseOrdersApi = new Resource("gestStock/purchase-orders");
export const receptionsApi     = new Resource("gestStock/receptions");

// ──────────────────────────────────────────────
// Fournisseurs = comptes flaggués is_fournisseur, servis par un annuaire
// stock en lecture seule (id + libellé). L'endpoint de gestion des comptes
// exige `browse comptes`, que le rôle « gestionnaire de stock » n'a pas.
// ──────────────────────────────────────────────
const suppliersResource = new Resource("gestStock/suppliers");

export function listFournisseurComptes(params = {}) {
    return suppliersResource.list(params);
}

// Clients : toujours servis par l'endpoint de gestion des comptes, donc
// soumis à `browse comptes`. Un porteur du seul rôle stock verra une liste
// vide sur la sortie de stock vers un client (client_id restant nullable).
const comptesResource = new Resource("comptes");

export function listClientComptes(params = {}) {
    return comptesResource.list({ ...params, account_type: "client" });
}

// ──────────────────────────────────────────────
// Dashboard
// ──────────────────────────────────────────────
export function getDashboardStats() {
    return request.get("/api/gestStock/dashboard/stats");
}

// ──────────────────────────────────────────────
// Product Models
// ──────────────────────────────────────────────
export function getModelsByBrand(brandId) {
    return request.get(`/api/gestStock/brands/${brandId}/product-models`);
}

// ──────────────────────────────────────────────
// Products — actions custom
// ──────────────────────────────────────────────
export function restoreProduct(id) {
    return request.post(`/api/gestStock/products/${id}/restore`);
}

export function getProductHistory(id, params = {}) {
    return request.get(`/api/gestStock/products/${id}/history`, { params });
}

export function importProducts(formData) {
    return request.post("/api/gestStock/products/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
    });
}

export function exportProducts(params = {}) {
    return request.get("/api/gestStock/products/export", {
        params,
        override_responseType: "blob"
    });
}

// ──────────────────────────────────────────────
// MAC addresses (rattachées à product_items)
// ──────────────────────────────────────────────
export function listMacs(productItemId) {
    return request.get(
        `/api/gestStock/product-items/${productItemId}/mac-addresses`
    );
}

export function addMac(productItemId, data) {
    return request.post(
        `/api/gestStock/product-items/${productItemId}/mac-addresses`,
        data
    );
}

export function deleteMac(macId) {
    return request.delete(`/api/gestStock/mac-addresses/${macId}`);
}

// ──────────────────────────────────────────────
// Storage locations
// ──────────────────────────────────────────────
export function getProductsAtLocation(locationId) {
    return request.get(`/api/gestStock/locations/${locationId}/products`);
}

// ──────────────────────────────────────────────
// Stock movements
// ──────────────────────────────────────────────
export function listMovements(params = {}) {
    return request.get("/api/gestStock/stock-movements", { params });
}

export function getMovement(id) {
    return request.get(`/api/gestStock/stock-movements/${id}`);
}

export function stockIn(data) {
    return request.post("/api/gestStock/stock/in", data);
}

export function stockOut(data) {
    return request.post("/api/gestStock/stock/out", data);
}

export function stockScanOut(data) {
    return request.post("/api/gestStock/stock/scan-out", data);
}

export function stockAdjust(data) {
    return request.post("/api/gestStock/stock/adjust", data);
}

export function stockReturn(data) {
    return request.post("/api/gestStock/stock/return", data);
}

export function sendToSav(data) {
    return request.post("/api/gestStock/stock/sav", data);
}

export function scrapItem(data) {
    return request.post("/api/gestStock/stock/scrap", data);
}

// ──────────────────────────────────────────────
// Purchase orders — actions custom
// ──────────────────────────────────────────────
export function markPurchaseOrderReceived(id) {
    return request.post(`/api/gestStock/purchase-orders/${id}/mark-received`);
}

export function cancelPurchaseOrder(id) {
    return request.post(`/api/gestStock/purchase-orders/${id}/cancel`);
}

export function getLateOrders() {
    return request.get("/api/gestStock/purchase-orders/report/late");
}

// ──────────────────────────────────────────────
// Réceptions de stock (bordereau + douchette)
// ──────────────────────────────────────────────
export function scanReception(receptionId, payload) {
    return request.post(`/api/gestStock/receptions/${receptionId}/scan`, payload);
}

export function closeReception(receptionId, payload = {}) {
    return request.post(`/api/gestStock/receptions/${receptionId}/close`, payload);
}

export function deleteReceptionLine(receptionId, lineId) {
    return request.delete(`/api/gestStock/receptions/${receptionId}/lines/${lineId}`);
}

// ──────────────────────────────────────────────
// Alerts
// ──────────────────────────────────────────────
export function listAlerts(params = {}) {
    return request.get("/api/gestStock/alerts", { params });
}

export function resolveAlert(id) {
    return request.post(`/api/gestStock/alerts/${id}/resolve`);
}

// ──────────────────────────────────────────────
// Reports
// ──────────────────────────────────────────────
export function reportCurrentStock(params = {}) {
    return request.get("/api/gestStock/reports/current-stock", { params });
}

export function reportLowStock() {
    return request.get("/api/gestStock/reports/low-stock");
}

export function reportMovements(params) {
    return request.get("/api/gestStock/reports/movements", { params });
}

export function reportStockValue() {
    return request.get("/api/gestStock/reports/stock-value");
}

// ──────────────────────────────────────────────
// Feuilles de comptage d'inventaire
//
// Le compte saisi n'ajuste rien : c'est `applyStockCount` qui écrit les écarts,
// par lot de 50 lignes côté serveur. Le client relance tant que
// `bilan.restantes` n'est pas à zéro — une feuille de 700 références ne tient
// pas les verrous de produits dans une seule requête.
// ──────────────────────────────────────────────
export function listStockCounts(params = {}) {
    return request.get("/api/gestStock/stock-counts", { params });
}

export function getStockCount(id) {
    // La feuille arrive entière, lignes et produits : sur une campagne de plusieurs
    // centaines de références, les 10 s par défaut expiraient après une validation
    // déjà passée, et l'écran gardait un bouton « Valider » sur une feuille close.
    return request.get(`/api/gestStock/stock-counts/${id}`, { override_timeout: 60000 });
}

export function openStockCount(data) {
    return request.post("/api/gestStock/stock-counts", data);
}

// `counted_quantity: null` retire le compte : une saisie erronée ne doit pas
// obliger à jeter toute la feuille.
export function countStockCountLine(countId, lineId, data) {
    return request.patch(`/api/gestStock/stock-counts/${countId}/lines/${lineId}`, data);
}

export function applyStockCount(id, data = {}) {
    // Un lot de 50 lignes peut créer des mouvements, évaluer les alertes et
    // recharger les stocks : 10 s (timeout par défaut) ne suffisent pas.
    return request.post(`/api/gestStock/stock-counts/${id}/apply`, data, { override_timeout: 90000 });
}

// Clôture d'une feuille que la validation n'a pas pu fermer elle-même (refus ou
// trous). `solde_empreinte` est celui que la feuille a rendu : le serveur le
// recalcule sous verrou et refuse si le solde a changé entre-temps — y compris
// s'il a été reconstruit avec les mêmes nombres.
export function closeStockCount(id, data) {
    return request.post(`/api/gestStock/stock-counts/${id}/close`, data);
}

export function cancelStockCount(id) {
    return request.post(`/api/gestStock/stock-counts/${id}/cancel`);
}
